import { Box, Tooltip } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { isMoment } from "moment";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import useGet from "../../services/hooks/useGet";
import usePost from "../../services/hooks/usePost";
import usePut from "../../services/hooks/usePut";
import { apiEndpoints } from "../../shared/constants/api-constant";
import { AlertType, MRSA, UserResponse } from "../../shared/constants/enums";
import {
  ctFormFields,
  ctInitForm,
  IForms,
  IUserForms,
} from "../../shared/constants/form.model";
import {
  Button,
  Heading,
  Input,
  SelectInput,
} from "../../shared/ReusableComponents";
import { DateTimeInput } from "../../shared/ReusableComponents/DateTimeInput";
import UploadPicture from "../../shared/ReusableComponents/UploadPicture";
import { CommonFunctions } from "../../shared/utils/CommonFunctions";
import {
  DateFormatter,
  DateTimeFormats,
} from "../../shared/utils/DateFormatter";
import { hideLoader, showLoader } from "../../store/currentPreferencesSlice";
import { toast } from "../../store/toastSlice";
import RadioWithInput from "../../shared/ReusableComponents/RadioWithInput";

export const CreateTemplate = () => {
  const { id } = useParams<{ id: string }>();
  const [submitted, setSubmitted] = useState(false);
  const errorText = "This Field is Mandatory";
  const [forms, setForms] = useState<any>(ctInitForm);
  const userForms = ctFormFields;
  const dispatch = useDispatch();
  const [base64Image, setBase64Image] = useState<string>("");
  const navigate = useNavigate();
  const { postService, loading: postLoading, data: postRes } = usePost();
  const { putService, loading: putLoading, data: putRes } = usePut();
  const { data, getService, loading } = useGet();

  const onChangeValues = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: IUserForms,
  ) => {
    if (field.key === "dob") {
      const dob = isMoment(e) && DateFormatter.formatDate(e.toISOString());
      if (dob === "Invalid date" || dob === "" || dob === false) {
        setForms({ ...forms, [field.key]: "", age: 0 });
      } else {
        const age = DateFormatter.getCurrentAge(dob);
        setForms({ ...forms, [field.key]: dob, age });
      }
      return;
    }
    if (field.type === "select" || field.type === "textarea") {
      setForms({ ...forms, [field.key]: e.target.value });
      return;
    }
    setForms({ ...forms, [e.target.name]: e.target.value });
  };

  const onSubmit = () => {
    setSubmitted(true);
    const isInfo = userForms.info.some(
      (field) => field.required && !forms[field.key],
    );
    const isDetails = userForms.details.some(
      (field) => field.required && !forms[field.key],
    );
    const isAlerts = userForms.alerts.some(
      (field) => field.required && !forms[field.key],
    );

    const isDescription =
      userForms.description.required && forms[userForms.description.key]
        ? false
        : true;

    if (isInfo || isDetails || isAlerts || isDescription) {
      return;
    }
    setSubmitted(false);
    let payload = { ...forms };
    payload.dob = DateFormatter.formatDateToISO(payload.dob);
    payload.isMRSA = payload.isMRSA === MRSA.POSITIVE;
    payload.isATSI = payload.isATSI === UserResponse.YES;
    payload.isInterpreterRequired =
      payload.isInterpreterRequired === UserResponse.YES;
    if (payload.image == null && payload.imageData != null) {
      payload.image = CommonFunctions.base64ToFile(payload.imageData);
    }
    delete payload.age;
    delete payload.imageData;

    const formData = new FormData();
    for (const key in payload) {
      if (payload.hasOwnProperty(key)) {
        formData.append(key, payload[key]);
      }
    }
    dispatch(showLoader());
    if (payload.id) {
      postService(apiEndpoints.UPDATE_TEMPLATE, formData, "");
    } else {
      delete payload.id;
      formData.delete("id");
      postService(apiEndpoints.CREATE_TEMPLATE, formData, "");
    }
  };

  useEffect(() => {
    !(loading || postLoading) && dispatch(hideLoader());
  }, [loading, postLoading]);

  useEffect(() => {
    !putLoading && dispatch(hideLoader());
  }, [putLoading]);

  useEffect(() => {
    if (postRes && postRes.templateId) {
      message(postRes.templateId);
      navigate(`/admin/template/${postRes.templateId}`);
    } else if (putRes && putRes === 200) {
      message(forms.id, true);
      navigate(`/admin/template/${forms.id}`);
    }
  }, [postRes, putRes]);

  const message = (id: number, isEdit = false) => {
    const fullId = CommonFunctions.ConvertIdToDisplayId(id, "template");
    dispatch(
      toast({
        isOpen: true,
        message: `${fullId} Template has been Successfully ${
          isEdit ? "Updated." : "Added."
        }`,
        type: AlertType.SUCCESS,
      }),
    );
  };

  useEffect(() => {
    if (id) {
      dispatch(showLoader());
      getService(`${apiEndpoints.TEMPLATE}/${id}`);
    }
  }, [id]);

  useEffect(() => {
    if (data) {
      let result = data as IForms;
      result = {
        ...result,
        isATSI: result.isATSI ? UserResponse.YES : UserResponse.NO,
        isMRSA: result.isMRSA ? MRSA.POSITIVE : MRSA.NEGATIVE,
        isInterpreterRequired: result.isInterpreterRequired
          ? UserResponse.YES
          : UserResponse.NO,
        ethinicity: result.ethinicity,
        dob: result.dob ? DateFormatter.formatDate(result.dob) : "",
        age: result.dob
          ? DateFormatter.getCurrentAge(DateFormatter.formatDate(result.dob))
          : 0,
      };
      setForms(result);
      setBase64Image(result.imageData);
    }
  }, [data]);

  return (
    <div className="createTemplate">
      <Grid container>
        <Grid size={forms.id ? 2 : 2.4}>
          {CommonFunctions.IsCurrentSuperUser() ? (
            <Heading
              text={forms.id ? "Edit Template" : "Create Template"}
              type="h3"
              isBold={true}
            />
          ) : (
            ""
          )}
        </Grid>
        <Grid size={forms.id ? 6.4 : 6}>
          <span className="grey-background">
            <Input
              name={userForms.description.key}
              label={userForms.description.label}
              value={forms[userForms.description.key]}
              size="small"
              required={true}
              disabled={false}
              onChange={(e: any) => onChangeValues(e, userForms.description)}
              slotProps={{
                htmlInput: { maxLength: userForms.description.max },
              }}
              hasError={
                submitted &&
                userForms.description.required &&
                !forms[userForms.description.key]
                  ? errorText
                  : false
              }
            />
          </span>
        </Grid>
      </Grid>
      <Grid
        container
        spacing={1}
        className={`box-grid-form topRadius ${
          submitted && !base64Image ? "fixed-error-text" : ""
        }`}
      >
        <Grid size="auto" className="mr-1">
          <Tooltip title="Please upload only JPG and PNG files">
            <div>
              <UploadPicture
                setSelectedImage={(e: any) => setForms({ ...forms, image: e })}
                imagePreview={
                  forms.imageData
                    ? `data:image/png;base64,${forms.imageData}`
                    : ""
                }
              />
            </div>
          </Tooltip>
        </Grid>
        <Grid size="grow">
          <span className="grey-light-background">
            <Grid container spacing={2}>
              {userForms.info &&
                userForms.info.map((field, index) =>
                  field.type === "text" ? (
                    <Grid key={index} size={field.size || 4}>
                      <Input
                        name={field.key}
                        label={field.label}
                        value={
                          field.key === "age" &&
                          !forms[field.key] &&
                          forms["dob"]
                            ? "0"
                            : forms[field.key]
                        }
                        size="small"
                        required={field.required}
                        disabled={field.disable}
                        classes={field.className}
                        slotProps={{
                          htmlInput: { maxLength: field.max ? field.max : "" },
                        }}
                        hasError={
                          submitted && field.required && !forms[field.key]
                            ? errorText
                            : false
                        }
                        onChange={(e: any) => onChangeValues(e, field)}
                      />
                    </Grid>
                  ) : field.type === "date" ? (
                    <Grid key={index} size={4}>
                      <DateTimeInput
                        type="date"
                        name={field.key}
                        format={DateTimeFormats.DateFormat}
                        value={forms[field.key]}
                        onChange={(e: any) => onChangeValues(e, field)}
                        required
                        size="small"
                        isDisabled={true}
                        disableFuture
                        hasError={
                          submitted && !forms[field.key] ? errorText : false
                        }
                        label={field.label}
                      />
                    </Grid>
                  ) : field.type === "select" ? (
                    <Grid key={index} size={4}>
                      <SelectInput
                        name={field.key}
                        label={field.label}
                        value={forms[field.key]}
                        onChange={(e: any) => onChangeValues(e, field)}
                        required={field.required}
                        size="small"
                        options={field.options}
                        hasError={
                          submitted && field.required && !forms[field.key]
                            ? errorText
                            : false
                        }
                      />
                    </Grid>
                  ) : null,
                )}
            </Grid>
          </span>
        </Grid>
      </Grid>
      <Grid spacing={1} className="box-grid bottomRadius mt-0">
        <Box className="details">
          <Heading text="Details" type="h5" isBold={true} />
          <Grid container spacing={2} className="mt-1">
            {userForms.details &&
              userForms.details.map((field, index) =>
                field.type === "text" ? (
                  <Grid key={index} size={6}>
                    <Input
                      name={field.key}
                      label={field.label}
                      value={forms[field.key]}
                      size="small"
                      required={field.required}
                      disabled={field.disable}
                      classes={field.className}
                      onChange={(e: any) => onChangeValues(e, field)}
                      hasError={
                        submitted && field.required && !forms[field.key]
                          ? errorText
                          : false
                      }
                      slotProps={{
                        htmlInput: { maxLength: field.max ? field.max : "" },
                        ...(field.key === "doctorName" && {
                          input: {
                            startAdornment: (
                              <span className="prefixName">Dr.</span>
                            ),
                          },
                        }),
                      }}
                    />
                  </Grid>
                ) : field.type === "select" ? (
                  <Grid key={index} size={6}>
                    <SelectInput
                      name={field.key}
                      label={field.label}
                      value={forms[field.key]}
                      onChange={(e: any) => onChangeValues(e, field)}
                      required={field.required}
                      size="small"
                      options={field.options}
                      hasError={
                        submitted && field.required && !forms[field.key]
                          ? errorText
                          : false
                      }
                    />
                  </Grid>
                ) : field.type === "radio" ? (
                  <Grid size={6}>
                    <Grid container spacing={2}>
                      <Grid size={5.5} gap={2}>
                        <Input
                          name="Ethinicity"
                          label="Ethnicity"
                          value={forms["ethinicity"]}
                          size="small"
                          required
                          onChange={(e) =>
                            setForms({ ...forms, ethinicity: e.target.value })
                          }
                          slotProps={{
                            htmlInput: { maxLength: 25 },
                          }}
                          hasError={
                            submitted && !forms["ethinicity"]
                              ? errorText
                              : false
                          }
                        />
                      </Grid>
                      <Grid size={6.5}>
                        <RadioWithInput
                          name={field.key}
                          label={field.label}
                          value={forms[field.key]}
                          options={field.options || []}
                          onChange={(e) => onChangeValues(e, field)}
                          required={field.required}
                          hasError={
                            submitted && field.required && !forms[field.key]
                              ? errorText
                              : false
                          }
                          disabled={field.disable}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                ) : null,
              )}
          </Grid>
        </Box>
        <Box className="alerts mt-1">
          <Heading text="Alerts" type="h5" isBold={true} />
          <Grid container spacing={2} className="mt-1">
            {userForms.alerts &&
              userForms.alerts.map((field, index) =>
                field.type === "select" ? (
                  <>
                    <Grid container key={index} size={6}>
                      <SelectInput
                        name={field.key}
                        label={field.label}
                        value={forms[field.key]}
                        onChange={(e: any) => onChangeValues(e, field)}
                        required={field.required}
                        size="small"
                        options={field.options}
                        hasError={
                          submitted && field.required && !forms[field.key]
                            ? errorText
                            : false
                        }
                      />
                    </Grid>
                    <Grid container key={4} size={6}></Grid>
                  </>
                ) : field.type === "textarea" ? (
                  <Grid key={index} size={6}>
                    <Input
                      name={field.key}
                      label={field.label}
                      value={forms[field.key]}
                      size="medium"
                      required={field.required}
                      disabled={field.disable}
                      classes={field.className}
                      minRows={3}
                      maxRows={3}
                      multiline={true}
                      slotProps={{
                        htmlInput: { maxLength: field.max ? field.max : "" },
                      }}
                      onChange={(e: any) => onChangeValues(e, field)}
                      hasError={
                        submitted && field.required && !forms[field.key]
                          ? errorText
                          : false
                      }
                    />
                  </Grid>
                ) : null,
              )}
          </Grid>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            marginTop: "1rem",
          }}
        >
          <Button
            text="Cancel"
            fullWidth={false}
            variant="outlined"
            type="button"
            onClick={(e) => {
              id ? navigate(`/admin/template/${id}`) : navigate("/admin");
            }}
          />
          <Button
            text={forms.id ? "Update" : "Create"}
            fullWidth={false}
            className="ml-1"
            onClick={onSubmit}
          />
        </Box>
      </Grid>
    </div>
  );
};
